class MegaMenu extends HTMLElement {
  currentClass = 'megamenu--current';
  visibleClass = 'megamenu--isVisible';
  animateClass = 'megamenu--animate';
  animateOutClass = 'megamenu--animateOut';
  noAnimationClass = 'megamenu--noAnimation';
  bodyClass = 'megamenu--isActive';
  menuElementClass = '[id^="shopify-section-sections"] #megamenu';
  mainNavTarget = 'header .header__menu-item.megamenu-item';
  mainNavListTarget = 'header .header__inline-menu .header__menu-item.megamenu-item';
  curtainTarget = 'mega-menu-curtain';
  menuElement = null;
  timer = null;
  scrollTimeout = null;
  menuLinkHoverDuration = 100;
  listenersAdded = false;
  activeTrigger = null;

  constructor() {
    super();

    this.onClickCurtain = this.onClickCurtain.bind(this);
    this.onMouseoverCurtain = this.onMouseoverCurtain.bind(this);
    this.onScrollWindow = this.onScrollWindow.bind(this);
    this.onMouseoverLink = this.onMouseoverLink.bind(this);
    this.onMouseoutLink = this.onMouseoutLink.bind(this);
    this.onFocusinDocument = this.onFocusinDocument.bind(this);
    this.onKeydownDocument = this.onKeydownDocument.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
    this.onResizeWindow = this.onResizeWindow.bind(this);

    this.initEventListeners();
    window.addEventListener('resize', this.onResizeWindow, { passive: true });
  }

  initEventListeners() {
    if (window.innerWidth >= 750 && !this.listenersAdded) {
      document.addEventListener('click', this.onClickCurtain);
      document.addEventListener('mouseover', this.onMouseoverCurtain);
      window.addEventListener('scroll', this.onScrollWindow, { passive: true });
      document.addEventListener('mouseover', this.onMouseoverLink);
      document.addEventListener('mouseout', this.onMouseoutLink);
      document.addEventListener('focusin', this.onFocusinDocument);
      document.addEventListener('keydown', this.onKeydownDocument);
      document.addEventListener('click', this.onClickOutside);
      this.listenersAdded = true;
    } else if (window.innerWidth < 750 && this.listenersAdded) {
      this.closeMenu();
      this.removeEventListeners();
    }
  }

  removeEventListeners() {
    document.removeEventListener('click', this.onClickCurtain);
    document.removeEventListener('mouseover', this.onMouseoverCurtain);
    window.removeEventListener('scroll', this.onScrollWindow);
    document.removeEventListener('mouseover', this.onMouseoverLink);
    document.removeEventListener('mouseout', this.onMouseoutLink);
    document.removeEventListener('focusin', this.onFocusinDocument);
    document.removeEventListener('keydown', this.onKeydownDocument);
    document.removeEventListener('click', this.onClickOutside);
    this.listenersAdded = false;
  }

  onResizeWindow() {
    this.initEventListeners();
  }

  connectedCallback() {
    if (window.innerWidth >= 750) {
      this.menuElement = document.querySelector(this.menuElementClass);
      if (this.menuElement) {
        this.menuElement.style.display = 'initial';
        this.addCurtain();
      }
    }
  }

  addCurtain() {
    if (document.getElementById('megamenu')) {
      return;
    }

    this.innerHTML = '<mega-menu-curtain></mega-menu-curtain>' + this.innerHTML;
  }

  removeAnimation() {
    if (!this.menuElement) {
      return;
    }

    this.menuElement.classList.remove(this.animateClass);
    document.body.classList.remove(this.bodyClass);

    setTimeout(() => {
      this.menuElement.classList.remove(this.animateOutClass);
    }, 350);
  }

  getMainNavItems() {
    return Array.from(document.querySelectorAll(this.mainNavListTarget));
  }

  getTriggerFromEventTarget(target) {
    return target?.closest(this.mainNavTarget) || null;
  }

  getPanelFromTrigger(triggerElement) {
    if (!triggerElement?.dataset?.title) {
      return null;
    }

    return document.getElementById(`megamenu-${triggerElement.dataset.title}`);
  }

  getPanelFocusableElements(panel) {
    if (!panel) return [];

    const selector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), mega-menu-button';
    return Array.from(panel.querySelectorAll(selector)).filter((element) => !element.hasAttribute('disabled') && element.offsetParent !== null);
  }

  setTriggerExpandedState(triggerElement, expanded) {
    if (!triggerElement?.hasAttribute('aria-controls')) {
      return;
    }

    triggerElement.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  showMenuItem(menuItemElement, options = {}) {
    if (!menuItemElement) {
      return;
    }

    if (!this.menuElement) {
      return;
    }

    const menuItem = this.getPanelFromTrigger(menuItemElement);
    const timerHoverDuration = options.instant ? 0 : (this.menuIsVisible() ? 0 : this.menuLinkHoverDuration);

    if (!menuItem) {
      this.closeMenu();
      return;
    }

    this.activeTrigger = menuItemElement;
    clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      // prepare menu for animation
      document.body.classList.add(this.bodyClass);
      this.menuElement.classList.add(this.animateClass);

      // show menu item
      setTimeout(() => {
        this.hideAllMenuItems();
        menuItemElement.classList.add(this.currentClass);
        menuItem.classList.add(this.visibleClass);
        menuItem.setAttribute('aria-hidden', 'false');
        this.setTriggerExpandedState(menuItemElement, true);

        setTimeout(() => {
          this.menuElement.classList.add(this.noAnimationClass);
        }, 200);
      }, timerHoverDuration)
    }, timerHoverDuration);
  }

  hideAllMenuItems() {
    this.getMainNavItems().forEach((item) => {
      item.classList.remove(this.currentClass);
      this.setTriggerExpandedState(item, false);
    });

    let visibleItems = document.querySelectorAll('.' + this.visibleClass);
    if (visibleItems) {
      for (let i = 0; i < visibleItems.length; i++) {
        let item = visibleItems[i];
        item.classList.remove(this.visibleClass);
        item.setAttribute('aria-hidden', 'true');
      }
    };
  }

  menuIsVisible() {
    return this.menuElement?.classList.contains(this.animateClass) || false;
  }

  closeMenu() {
    if (!this.menuElement) {
      return;
    }

    clearTimeout(this.timer);
    this.menuElement.classList.remove(this.noAnimationClass);
    this.hideAllMenuItems();
    this.removeAnimation();
  }

  onClickCurtain(e) {
    if (!e.target.closest(this.curtainTarget)) {
      return false;
    }

    this.closeMenu();
  }

  onMouseoverCurtain(e) {
    if (!e.target.closest(this.curtainTarget)) {
      return false;
    }
    if (!this.menuIsVisible()) {
      return;
    }

    this.closeMenu();
  }

  onScrollWindow() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.closeMenu();
    }, 10);
  }

  onMouseoverLink(e) {
    const trigger = this.getTriggerFromEventTarget(e.target);
    if (!trigger) {
      return false;
    }

    this.showMenuItem(trigger);
  }

  onMouseoutLink() {
    clearTimeout(this.timer);
  }

  onFocusinDocument(e) {
    const trigger = this.getTriggerFromEventTarget(e.target);
    if (trigger) {
      this.showMenuItem(trigger, { instant: true });
      return;
    }

    if (e.target.closest('#megamenu')) {
      return;
    }

    if (this.menuIsVisible()) {
      this.closeMenu();
    }
  }

  focusAdjacentTrigger(currentTrigger, direction) {
    const triggers = this.getMainNavItems();
    if (!triggers.length) {
      return;
    }

    const currentIndex = triggers.indexOf(currentTrigger);
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = (currentIndex + direction + triggers.length) % triggers.length;
    triggers[nextIndex].focus();
    this.showMenuItem(triggers[nextIndex], { instant: true });
  }

  onKeydownDocument(e) {
    if (window.innerWidth < 750) {
      return;
    }

    const trigger = this.getTriggerFromEventTarget(e.target);

    if (e.key === 'Escape') {
      if (this.menuIsVisible()) {
        e.preventDefault();
        this.closeMenu();

        if (this.activeTrigger) {
          this.activeTrigger.focus();
        }
      }
      return;
    }

    if (!trigger) {
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.focusAdjacentTrigger(trigger, 1);
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.focusAdjacentTrigger(trigger, -1);
      return;
    }

    if (e.key === 'ArrowDown') {
      const controlledPanel = this.getPanelFromTrigger(trigger);
      if (!controlledPanel) {
        return;
      }

      e.preventDefault();
      this.showMenuItem(trigger, { instant: true });

      const focusableElements = this.getPanelFocusableElements(controlledPanel);
      if (focusableElements.length) {
        focusableElements[0].focus();
      }
    }
  }

  onClickOutside(e) {
    if (e.target.closest(this.mainNavTarget) || e.target.closest('#megamenu')) {
      return;
    }

    if (this.menuIsVisible()) {
      this.closeMenu();
    }
  }
};

window.customElements.define("mega-menu", MegaMenu);

window.customElements.define("mega-menu-button", class Megamenu extends HTMLElement {
    constructor() {
      super();

      this.onMouseover = this.onMouseover.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.setAttribute('role', 'button');
      this.setAttribute('aria-selected', this.classList.contains('active') ? 'true' : 'false');
      this.setAttribute('tabindex', this.classList.contains('active') ? '0' : '-1');

      this.addEventListener('mouseover', this.onMouseover);
      this.addEventListener('focus', this.onFocus);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      if (this.classList.contains('active')) {
        this.activate();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('mouseover', this.onMouseover);
      this.removeEventListener('focus', this.onFocus);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    getSiblingButtons() {
      const menuItem = this.closest('mega-menu-item');
      if (!menuItem) {
        return [];
      }

      return Array.from(menuItem.querySelectorAll('mega-menu-button'));
    }

    activate() {
      const menuItem = this.closest('mega-menu-item');
      if (!menuItem) {
        return;
      }

      menuItem.querySelectorAll('mega-menu-columns').forEach((column) => {
        column.style.display = 'none';
        column.setAttribute('aria-hidden', 'true');
      });

      this.getSiblingButtons().forEach((button) => {
        button.classList.remove('active');
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('tabindex', '-1');
      });

      const element = menuItem.querySelector(`[data-type="${this.dataset.trigger}"]`);
      if (element) {
        element.style.display = '';
        element.setAttribute('aria-hidden', 'false');
      }

      this.classList.add('active');
      this.setAttribute('aria-selected', 'true');
      this.setAttribute('tabindex', '0');
    }

    focusSiblingButton(direction) {
      const buttons = this.getSiblingButtons();
      if (!buttons.length) {
        return;
      }

      const currentIndex = buttons.indexOf(this);
      if (currentIndex < 0) {
        return;
      }

      const nextIndex = (currentIndex + direction + buttons.length) % buttons.length;
      buttons[nextIndex].focus();
      buttons[nextIndex].activate();
    }

    onMouseover() {
      this.activate();
    }

    onFocus() {
      this.activate();
    }

    onClick() {
      if (this.dataset.href) {
        window.location.href = this.dataset.href;
      }
    }

    onKeydown(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.focusSiblingButton(1);
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.focusSiblingButton(-1);
        return;
      }

      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        this.activate();
      }
    }
})
